import React, { useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { isDefined } from '../../../../../utils/variables';
import { useGsdUnit } from '../../../../hooks/use-gsd-unit';
import { GeodataSetupContext } from '../../../../state/geodata-setup-state';
import { InfoBox } from '../../../info-box/info-box';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { translatedProcessType } from '../../geodata';
import { ImageThresholdExceededInfoBox } from './image-threshold-exceeded-info-box';

export const GeodataSummary = () => {
  const { t } = useTranslation('cloudProcessing');
  const context = useContext(GeodataSetupContext);
  const gsdUnit = useGsdUnit();

  // Get enabled addon names
  const enabledAddons = useMemo(() => {
    if (!isDefined(context)) {
      return [];
    }
    const addonNames: string[] = [];
    if (context.addons?.terrainModel?.enabled) {
      addonNames.push(t('setupDialog.addOns.terrainModel.cardTitle', { ns: 'cloudProcessing' }));
    }
    if (context.addons?.gcpAdjustment?.enabled) {
      addonNames.push(t('setupDialog.addOns.gcpAdjustment.cardTitle', { ns: 'cloudProcessing' }));
    }
    if (context.addons?.filteredPointCloud?.enabled) {
      addonNames.push(
        t('setupDialog.addOns.filteredPointCloud.cardTitle', { ns: 'cloudProcessing' }),
      );
    }
    return addonNames;
  }, [context, t]);

  if (!isDefined(context)) {
    return null;
  }

  const { geodataConfiguration, gcpPoints, boundaryFiles, imageFiles } = context;

  return (
    <Container>
      <h4>{t('geodataSetup.summary.title', { ns: 'cloudProcessing' })}</h4>
      <Content>
        <Stack spacing={1}>
          {context.useInteractiveSetup ? (
            <InfoBox bottomMargin={true} color="yellow" leftIcon={{ icon: 'info-circle' }}>
              <Trans
                components={{ bold: <strong />, br: <br /> }}
                i18nKey="geodataSetup.summary.interactiveSetupInfo"
                ns="cloudProcessing"
              />
            </InfoBox>
          ) : context.imageFiles.length > 2000 ? (
            <ImageThresholdExceededInfoBox />
          ) : null}
          <SectionsGrid>
            <ItemsGrid>
              <LabelledContainer
                fontWeight={500}
                text={t('geodataPage.details.package', { ns: 'cloudProcessing' })}
              >
                {translatedProcessType(context.selectedPackage, t)}
              </LabelledContainer>
              <LabelledContainer fontWeight={500} text={t('name', { ns: 'common' })}>
                {geodataConfiguration.name}
              </LabelledContainer>
              {context.selectedPackage !== 'PM' && (
                <LabelledContainer
                  fontWeight={500}
                  text={t('settings.gsd', { ns: 'cloudProcessing', unit: `${gsdUnit}/px` })}
                >
                  {geodataConfiguration.gsd}
                </LabelledContainer>
              )}
              <LabelledContainer
                fontWeight={500}
                text={t('geodataSetup.summary.numberOfImagesAdded', { ns: 'cloudProcessing' })}
              >
                {imageFiles.length}
              </LabelledContainer>
              {context.gcpEnabled && (
                <LabelledContainer
                  fontWeight={500}
                  text={t('geodataPage.details.gcpCount', { ns: 'cloudProcessing' })}
                >
                  {gcpPoints.length}
                </LabelledContainer>
              )}
              <LabelledContainer
                fontWeight={500}
                text={t('geodataSetup.summary.numberOfBoundaryPolygonsAdded', {
                  ns: 'cloudProcessing',
                })}
              >
                {boundaryFiles.length}
              </LabelledContainer>
              {context.selectedPackage !== 'PM' && (
                <LabelledContainer
                  fontWeight={500}
                  text={t('setupDialog.rollingShutter', { ns: 'cloudProcessing' })}
                >
                  {geodataConfiguration.rollingShutter
                    ? t('yes', { ns: 'common' })
                    : t('no', { ns: 'common' })}
                </LabelledContainer>
              )}
              {geodataConfiguration.objMaxTriangles && (
                <LabelledContainer
                  fontWeight={500}
                  text={t('settings.objMaxTriangles', { ns: 'cloudProcessing' })}
                >
                  {geodataConfiguration.objMaxTriangles}
                </LabelledContainer>
              )}
            </ItemsGrid>

            {enabledAddons.length > 0 && (
              <LabelledContainer
                fontWeight={500}
                text={t('geodataPage.details.addOnsTitle', { ns: 'cloudProcessing' })}
              >
                <AddonList>
                  {enabledAddons.map((addonName, index) => (
                    <AddonItem key={index}>{addonName}</AddonItem>
                  ))}
                </AddonList>
              </LabelledContainer>
            )}
          </SectionsGrid>
        </Stack>
      </Content>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

const Content = styled.div`
  padding: 1rem;
`;

const SectionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
`;

const AddonList = styled.ul`
  margin: 0;
  padding-left: 1.5rem;
`;

const AddonItem = styled.li`
  margin: 0.25rem 0;
`;
